import { useState, useRef, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { LucideIcon } from '@/components/ui/icon-picker'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Edit, Trash2, MoreHorizontal } from 'lucide-react'
import { AuthorizationGate } from '@/features/authorization/AuthorizationGate'

// ── Types ──

export interface DomainCardData {
    id: string
    name: string
    description: string
    icon?: string
    pass_threshold?: number
    question_count?: number
    created_at?: string
    updated_at?: string
}

interface DomainCardProps {
    domain: DomainCardData
    onEdit: (domain: DomainCardData) => void
    onDelete: (id: string) => void
}

// ── Component ──

export function DomainCard({ domain, onEdit, onDelete }: DomainCardProps) {
    const [menuOpen, setMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!menuOpen) return
        const handleClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as HTMLElement)) {
                setMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [menuOpen])

    const questionCount = domain.question_count ?? 0

    return (
        <Card className="group hover:shadow-md transition-shadow">
            <CardContent className="p-4 flex items-start gap-4">
                {/* Icon */}
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                    <LucideIcon name={domain.icon || 'FileText'} className="h-5 w-5 text-blue-600" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900 truncate mb-0.5">{domain.name}</h4>
                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                        {domain.description || 'No description'}
                    </p>
                <div className="mt-1.5 flex items-center gap-2 text-[11px] text-gray-400">
                    <span>{questionCount} {questionCount === 1 ? 'question' : 'questions'}</span>
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                        Pass Rate: {Math.round((domain.pass_threshold ?? 0.70) * 100)}%
                    </span>
                </div>
                </div>

                {/* Actions Menu */}
                <div className="relative shrink-0" ref={menuRef}>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                    {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 w-36 bg-white border rounded-md shadow-lg z-50 py-1">
                            <AuthorizationGate actions={['update_domain']}>
                                <button
                                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    onClick={() => { setMenuOpen(false); onEdit(domain) }}
                                >
                                    <Edit className="h-3.5 w-3.5" /> Edit
                                </button>
                            </AuthorizationGate>
                            <AuthorizationGate actions={['delete_domain']}>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <button
                                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" /> Delete
                                        </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete Domain</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Are you sure you want to delete domain "{domain.name}"? This action cannot be undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                                onClick={() => { setMenuOpen(false); onDelete(domain.id) }}
                                                className="bg-red-600 hover:bg-red-700"
                                            >
                                                Delete
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </AuthorizationGate>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
